import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import parse from 'html-react-parser';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Loader } from 'lucide-react';

import { Button } from 'components/ui/button';
import { useGetAIReportDetail } from 'services/personalised-ai-report/hooks';
import { PATH_ROUTES } from 'routes/path';
import { ScrollArea } from 'components/ui/scroll-area';
import { Progress } from 'components/ui/progress';

import ReportLoader from 'assets/report_loader.png';
import useProgressTimer from 'hooks/useProgressTimer';

interface FinalReportProps {
  tokenId: string;
  retry: number;
  retryDelay: number;
  message: string | undefined;
}

export default function FinalReport({
  tokenId,
  retry,
  retryDelay,
  message,
}: FinalReportProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [isDelayed, setIsDelayed] = useState<boolean>(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const progress = useProgressTimer(retry, retryDelay);

  const { data: reportDetail, isError } = useGetAIReportDetail({
    tokenId,
    gcTime: 0,
    staleTime: 0,
    initialData: [],
    retry,
    retryDelay,
    enabled: retry > 0,
  });

  useEffect(() => {
    if (isError) {
      toast.error(t('failed_to_generate_report_msg'));
    }

    timeoutRef.current = setTimeout(
      () => setIsDelayed(true),
      retry * retryDelay + 2000
    );

    return () => {
      if (timeoutRef?.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (reportDetail?.[0]?.ai_response && timeoutRef?.current) {
      clearTimeout(timeoutRef.current);
    }
  }, [reportDetail, timeoutRef]);

  if (retry <= 0) {
    return (
      <div className='flex flex-col items-center justify-center w-full h-[calc(100vh-16rem)] lg:h-full px-4 space-y-10'>
        <p className='text-base font-medium text-center whitespace-pre'>
          {message}
        </p>
        <Button
          variant='outline'
          className='h-8 px-6 text-gray-700 border-gray-700 rounded-full'
          onClick={() => navigate(PATH_ROUTES.PERSONALISED_AI_REPORT)}
        >
          {t('exit')}
        </Button>
      </div>
    );
  }

  if (isError || isDelayed) {
    return (
      <div className='flex flex-col items-center justify-center w-full h-full px-4 space-y-4'>
        <p className='text-base font-medium text-center'>
          {isDelayed
            ? t('report_generation_delay_msg')
            : t('failed_to_generate_ai_report')}
        </p>
        <Button
          variant='outline'
          className='h-8 px-6 text-gray-700 border-gray-700 rounded-full'
          onClick={() => navigate(PATH_ROUTES.PERSONALISED_AI_REPORT)}
        >
          {t('back')}
        </Button>
      </div>
    );
  }

  if (reportDetail?.[0]?.ai_response) {
    return (
      <div className='max-w-3xl py-4 mx-auto'>
        <h1 className='px-4 mb-2 text-xl font-semibold text-center sm:text-2xl text-primary'>
          {t('personalised_health_report')}
        </h1>

        <ScrollArea className='h-[calc(100vh-14rem)]'>
          {parse(reportDetail[0].ai_response)}
        </ScrollArea>

        <div className='flex justify-center mt-4'>
          <Button
            variant='outline'
            className='h-8 px-6 text-gray-700 border-gray-700 rounded-full'
            onClick={() => navigate(PATH_ROUTES.PERSONALISED_AI_REPORT)}
          >
            {t('exit')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className='flex items-center justify-center mb-6'>
      <div className='max-w-lg mx-4 space-y-4 lg:mx-auto'>
        <ScrollArea className='h-fit sm:h-[calc(100vh-11rem)]'>
          <div className='space-y-4'>
            <div className='relative mx-auto size-60'>
              <img
                src={ReportLoader}
                alt='loader'
                className='object-cover w-full h-full'
              />
              <div className='absolute z-50 left-[38%] top-[36%]'>
                <Loader className='text-white size-6' />
              </div>
            </div>

            <div className='w-2/3 mx-auto mb-4'>
              <Progress value={progress} className='w-full' />
            </div>

            <div>
              <h2 className='text-lg font-semibold'>
                {t('wait_for_process_data')}
              </h2>
              <p className='whitespace-pre-wrap'>
                {t('generating_health_report')}
              </p>
              <h2 className='mt-2 text-lg font-semibold'>
                {t('report_delivery')}
              </h2>
              <p>{t('email_convenience')}</p>
            </div>
          </div>
        </ScrollArea>

        <div className='flex justify-center w-full gap-x-4'>
          <Button
            variant='outline'
            className='h-8 px-6 text-gray-700 border-gray-700 rounded-full'
            onClick={() => navigate(PATH_ROUTES.PERSONALISED_AI_REPORT)}
          >
            {t('exit')}
          </Button>
        </div>
      </div>
    </div>
  );
}
